import { useState, useCallback } from 'react';
import { apiFetch } from '../scripts/api';
import { __, sprintf } from '@wordpress/i18n';

const FIELDS_TO_IMPORT = [
	'post_title',
	'_or_description',
	'_or_sector',
	'_or_field',
	'_or_location',
	'_or_territory',
	'_or_city',
	'_or_address',
	'_or_person_ref_tmp',
	'_or_contact_mail',
	'_or_contact_phone',
	'_or_contact_website',
	'_or_contact_social',
	'_or_contact_social₂',
	'_or_legal_form',
	'_or_year_establishment',
	'_or_network',
	'_or_anagos_membership',
	'_or_anagos_year_membership',
	'_or_audit',
	'_or_social_market',
];

const ACTION_LABELS = {
	create: __( 'New', 'anacrm' ),
	update: __( 'Update', 'anacrm' ),
	skip: __( 'Skip', 'anacrm' ),
	invalid: __( 'Invalid', 'anacrm' ),
};

const ACTION_CLASSES = {
	create: 'bg-green text-black',
	update: 'bg-yellow text-black',
	skip: 'bg-gray-light text-black',
	invalid: 'bg-red text-white',
};

const CHUNK_SIZE = 50;

function nonce() {
	return window.anacrm?.nonce ?? '';
}

export default function OrgImporter() {
	const [ stage, setStage ] = useState( 'select' ); // select | preview | importing | done
	const [ file, setFile ] = useState( null );
	const [ testing, setTesting ] = useState( false );
	const [ error, setError ] = useState( null );
	const [ preview, setPreview ] = useState( null ); // { token, total, counts, rows }
	const [ progress, setProgress ] = useState( { processed: 0, total: 0 } );
	const [ result, setResult ] = useState( null ); // { created, updated, skipped, errors }

	const reset = useCallback( () => {
		setStage( 'select' );
		setFile( null );
		setError( null );
		setPreview( null );
		setProgress( { processed: 0, total: 0 } );
		setResult( null );
	}, [] );

	const handleTest = async () => {
		if ( ! file ) {
			return;
		}
		setTesting( true );
		setError( null );
		try {
			const formData = new FormData();
			formData.append( 'file', file );
			// Plain fetch (not apiFetch): apiFetch always forces a JSON
			// Content-Type header, which breaks a multipart file upload.
			const res = await fetch( '/wp-json/anacrm/v1/org/import/preview', {
				method: 'POST',
				headers: { 'X-WP-Nonce': nonce() },
				body: formData,
			} );
			const json = await res.json().catch( () => ( {} ) );
			if ( ! res.ok ) {
				throw new Error( json.message || `HTTP ${ res.status }` );
			}
			setPreview( json );
			setStage( 'preview' );
		} catch ( err ) {
			setError( err.message );
		} finally {
			setTesting( false );
		}
	};

	const handleImport = async () => {
		if ( ! preview ) {
			return;
		}
		setStage( 'importing' );
		setError( null );
		let offset = 0;
		const totals = { created: 0, updated: 0, skipped: 0, errors: [] };
		try {
			while ( offset < preview.total ) {
				const chunk = await apiFetch( '/anacrm/v1/org/import/commit', {
					method: 'POST',
					body: JSON.stringify( {
						token: preview.token,
						offset,
						limit: CHUNK_SIZE,
					} ),
				} );
				totals.created += chunk.created;
				totals.updated += chunk.updated;
				totals.skipped += chunk.skipped;
				totals.errors.push( ...chunk.errors );
				offset = chunk.processed;
				setProgress( {
					processed: chunk.processed,
					total: chunk.total,
				} );
			}
			setResult( totals );
			setStage( 'done' );
		} catch ( err ) {
			setError( err.message );
			setStage( 'preview' );
		}
	};

	if ( stage === 'preview' && preview ) {
		return (
			<div className="flex flex-col gap-4">
				<h2 className="text-xl font-bold">
					{ __( 'Review import', 'anacrm' ) }
				</h2>
				<div className="text-sm flex flex-wrap gap-4">
					<span>
						{ preview.counts.create } { __( 'new', 'anacrm' ) }
					</span>
					<span>
						{ preview.counts.update }{ ' ' }
						{ __( 'to update', 'anacrm' ) }
					</span>
					<span>
						{ preview.counts.skip } { __( 'skipped', 'anacrm' ) }
					</span>
					<span>
						{ preview.counts.invalid } { __( 'invalid', 'anacrm' ) }
					</span>
				</div>
				{ error && (
					<div className="px-4 py-3 border-2 border-red text-red text-sm">
						{ error }
					</div>
				) }
				<div className="overflow-x-auto max-h-[500px] border-2 border-black">
					<table className="w-full border-collapse text-xs">
						<thead>
							<tr className="bg-gray-light">
								<th className="px-3 py-2 text-left">
									{ __( 'Row', 'anacrm' ) }
								</th>
								<th className="px-3 py-2 text-left">
									{ __( 'Name', 'anacrm' ) }
								</th>
								<th className="px-3 py-2 text-left">
									{ __( 'Sector', 'anacrm' ) }
								</th>
								<th className="px-3 py-2 text-left">
									{ __( 'Category', 'anacrm' ) }
								</th>
								<th className="px-3 py-2 text-left">
									{ __( 'Legal form', 'anacrm' ) }
								</th>
								<th className="px-3 py-2 text-left">
									{ __( 'City', 'anacrm' ) }
								</th>
								<th className="px-3 py-2 text-left">
									{ __( 'Action', 'anacrm' ) }
								</th>
								<th className="px-3 py-2 text-left">
									{ __( 'Details', 'anacrm' ) }
								</th>
							</tr>
						</thead>
						<tbody>
							{ preview.rows.map( ( r ) => (
								<tr
									key={ r.row }
									className="border-t border-gray-light"
								>
									<td className="px-3 py-2">{ r.row }</td>
									<td className="px-3 py-2">
										{ r.post_title }
									</td>
									<td className="px-3 py-2">
										{ r._or_sector }
									</td>
									<td className="px-3 py-2">
										{ r._or_field }
									</td>
									<td className="px-3 py-2">
										{ r._or_legal_form }
									</td>
									<td className="px-3 py-2">
										{ r._or_city }
									</td>
									<td className="px-3 py-2">
										<span
											className={ `inline-block px-2 py-0.5 font-semibold ${
												ACTION_CLASSES[ r.action ]
											}` }
										>
											{ ACTION_LABELS[ r.action ] }
										</span>
									</td>
									<td className="px-3 py-2">
										{ r.action === 'update' && (
											<div>{ r.matchLabel }</div>
										) }
										{ ( r.action === 'skip' ||
											r.action === 'invalid' ) && (
											<div>{ r.reason }</div>
										) }
										{ r.warnings?.length > 0 && (
											<ul className="list-disc pl-4 text-[11px] text-gray-dark">
												{ r.warnings.map( ( w, i ) => (
													<li key={ i }>{ w }</li>
												) ) }
											</ul>
										) }
									</td>
								</tr>
							) ) }
						</tbody>
					</table>
				</div>
				<div className="flex gap-2">
					<button
						onClick={ handleImport }
						disabled={
							preview.counts.create + preview.counts.update === 0
						}
						className="px-4 py-2 border-2 border-green-dark bg-green hover:bg-yellow cursor-pointer font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
					>
						{ __( 'Complete import', 'anacrm' ) }
					</button>
					<button
						onClick={ reset }
						className="px-4 py-2 border-2 border-black bg-white hover:bg-gray-light cursor-pointer font-bold text-sm"
					>
						{ __( 'Cancel', 'anacrm' ) }
					</button>
				</div>
			</div>
		);
	}

	if ( stage === 'importing' ) {
		const pct = progress.total
			? Math.round( ( progress.processed / progress.total ) * 100 )
			: 0;
		return (
			<div className="flex flex-col gap-4">
				<h2 className="text-xl font-bold">
					{ __( 'Importing…', 'anacrm' ) }
				</h2>
				<div className="w-full h-4 border-2 border-black bg-white">
					<div
						className="h-full bg-green"
						style={ { width: `${ pct }%` } }
					/>
				</div>
				<p className="text-sm">
					{ progress.processed } { __( 'of', 'anacrm' ) }{ ' ' }
					{ progress.total } { __( 'records processed', 'anacrm' ) }
				</p>
			</div>
		);
	}

	if ( stage === 'done' && result ) {
		return (
			<div className="flex flex-col gap-4">
				<h2 className="text-xl font-bold">
					{ __( 'Import complete', 'anacrm' ) }
				</h2>
				<div className="text-sm flex flex-wrap gap-4">
					<span>
						{ result.created } { __( 'created', 'anacrm' ) }
					</span>
					<span>
						{ result.updated } { __( 'updated', 'anacrm' ) }
					</span>
					<span>
						{ result.skipped } { __( 'skipped', 'anacrm' ) }
					</span>
				</div>
				{ result.errors.length > 0 && (
					<div className="text-sm">
						<strong>{ __( 'Rows with issues:', 'anacrm' ) }</strong>
						<ul className="list-disc pl-5">
							{ result.errors.map( ( e, i ) => (
								<li key={ i }>
									{ __( 'Row', 'anacrm' ) } { e.row }:{ ' ' }
									{ e.reason }
								</li>
							) ) }
						</ul>
					</div>
				) }
				<div>
					<a
						href={ window.location.pathname }
						className="inline-block px-4 py-2 border-2 border-green-dark bg-gray-light hover:bg-yellow cursor-pointer !no-underline font-bold text-sm"
					>
						{ __( 'Back to list', 'anacrm' ) }
					</a>
				</div>
			</div>
		);
	}

	// stage === 'select'
	return (
		<div className="flex flex-col gap-4">
			<h2 className="text-xl font-bold">
				{ __( 'Import entities from CSV', 'anacrm' ) }
			</h2>
			<div className="text-sm">
				<h3 className="font-semibold mb-1">
					{ __( 'How to use the importer', 'anacrm' ) }
				</h3>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						{ sprintf(
							// translators: %s is a comma-separated list of CSV column names.
							__(
								'The following columns can be imported if present: %s.',
								'anacrm'
							),
							FIELDS_TO_IMPORT.join( ', ' )
						) }
					</li>
					<li>
						{ __(
							'Columns must be named exactly as listed above.',
							'anacrm'
						) }
					</li>
					<li>
						{ __(
							'Any other columns in the file are ignored.',
							'anacrm'
						) }
					</li>
					<li>
						{ __(
							'Existing entities are matched by name (post title) and updated instead of duplicated.',
							'anacrm'
						) }
					</li>
					<li>
						{ __(
							'Taxonomy values (sector, category, legal form, territory, city, network) are matched against existing terms case/accent-insensitively; unmatched values create new terms, listed as warnings in the preview.',
							'anacrm'
						) }
					</li>
				</ul>
			</div>
			<fieldset className="max-w-[250px]">
				<input
					type="file"
					accept=".csv,text/csv"
					onChange={ ( e ) =>
						setFile( e.target.files?.[ 0 ] ?? null )
					}
					className="text-sm px-4 py-2 border-2 border-green-dark bg-gray-light hover:bg-yellow cursor-pointer"
				/>
			</fieldset>
			{ error && (
				<div className="px-4 py-3 border-2 border-red text-red text-sm">
					{ error }
				</div>
			) }
			<div>
				<button
					onClick={ handleTest }
					disabled={ ! file || testing }
					className="px-4 py-2 border-2 border-green-dark bg-green hover:bg-yellow cursor-pointer font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
				>
					{ testing
						? __( 'Testing…', 'anacrm' )
						: __( 'Test file', 'anacrm' ) }
				</button>
			</div>
		</div>
	);
}
